import { useCallback, useMemo, useState } from "react";
import { CookieConsentContext } from "./cookie-consent.context";
import { getRoutes } from "../../../config";
import { useModal } from "../../../elements";

import type {
  CookieConfig,
  CookieConsentProviderProps,
} from "../cookie-consent.types";

export function CookieConsentProvider({
  baseURL,
  saveConsents,
  getConsents,
  pageInteraction,
  openInteraction,
  children,
}: Readonly<CookieConsentProviderProps>) {
  const [cookieConfig, setCookieConfig] = useState<CookieConfig | null>(null);
  const {
    isOpen: isModalOpen,
    closeModal,
    openModal,
  } = useModal("CookieControlModal");
  const routes = getRoutes(baseURL);

  const handlePageInteraction = useCallback(
    (text: string) => pageInteraction?.(text),
    [pageInteraction]
  );

  const handleOpenInteraction = useCallback(() => {
    if (openInteraction) {
      openInteraction();
    }
  }, [openInteraction]);

  const fetchCookieConfig = useCallback(async () => {
    const consents = await getConsents();
    setCookieConfig(consents);
  }, [getConsents]);

  const handleSaveConsent = useCallback(async () => {
    if (!cookieConfig) return;

    const consents = {
      ...cookieConfig,
      showCookieNotice: false,
    };

    await saveConsents(consents);
    setCookieConfig(consents);
  }, [cookieConfig, saveConsents]);

  const value = useMemo(
    () => ({
      cookieConfig,
      setCookieConfig,
      routes,
      isModalOpen,
      openModal,
      closeModal,
      handleSaveConsent,
      fetchCookieConfig,
      pageInteraction: handlePageInteraction,
      openInteraction: handleOpenInteraction,
    }),
    [
      cookieConfig,
      routes,
      isModalOpen,
      openModal,
      closeModal,
      handleSaveConsent,
      fetchCookieConfig,
      pageInteraction,
      openInteraction
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}
